import type {
  OwnerDeviceState,
  ShareState,
  TransferState,
  VideoCapabilityState,
  ViewerActivityState,
  ViewerAdmissionState,
} from "./states";

export const FIXTURE_IDS = Object.freeze({
  ownerDevice: "00000000-0000-4000-8000-000000000001",
  shareA: "00000000-0000-4000-8000-000000000010",
  shareB: "00000000-0000-4000-8000-000000000011",
  mediaImage: "00000000-0000-4000-8000-000000000020",
  mediaVideo: "00000000-0000-4000-8000-000000000021",
  transfer: "00000000-0000-4000-8000-000000000030",
} as const);

export interface FixtureShare {
  readonly shareId: string;
  readonly label: string;
  readonly state: ShareState;
  readonly viewerCount: number;
  readonly access: "view" | "download";
}

export interface OwnerScreenFixture {
  readonly fixtureId: string;
  readonly ownerDeviceState: OwnerDeviceState;
  readonly selectedShareId: string;
  readonly shares: readonly FixtureShare[];
  readonly videoCapability: VideoCapabilityState;
}

export interface ViewerScreenFixture {
  readonly fixtureId: string;
  readonly admissionState: ViewerAdmissionState;
  readonly activityState: ViewerActivityState;
  readonly selectedShareId: string | null;
  readonly shares: readonly FixtureShare[];
  readonly transferState: TransferState | null;
  readonly transferProgress: number | null;
  readonly videoCapability: VideoCapabilityState;
}

const BASE_SHARES: readonly FixtureShare[] = Object.freeze([
  Object.freeze({
    shareId: FIXTURE_IDS.shareA,
    label: "Winter garden",
    state: "active",
    viewerCount: 2,
    access: "download",
  }),
  Object.freeze({
    shareId: FIXTURE_IDS.shareB,
    label: "Family afternoon",
    state: "available",
    viewerCount: 0,
    access: "view",
  }),
]);

export function createOwnerScreenFixture(
  overrides: Partial<OwnerScreenFixture> = {},
): OwnerScreenFixture {
  return {
    fixtureId: "owner.default",
    ownerDeviceState: "available",
    selectedShareId: FIXTURE_IDS.shareA,
    shares: BASE_SHARES,
    videoCapability: "hidden",
    ...overrides,
  };
}

export function createViewerScreenFixture(
  overrides: Partial<ViewerScreenFixture> = {},
): ViewerScreenFixture {
  return {
    fixtureId: "viewer.default",
    admissionState: "connected",
    activityState: "ready",
    selectedShareId: FIXTURE_IDS.shareA,
    shares: BASE_SHARES,
    transferState: null,
    transferProgress: null,
    videoCapability: "hidden",
    ...overrides,
  };
}

export const OWNER_DEVICE_FIXTURES: Readonly<
  Record<OwnerDeviceState, OwnerScreenFixture>
> = Object.freeze({
  available: createOwnerScreenFixture({
    fixtureId: "owner.device.available",
    ownerDeviceState: "available",
  }),
  reconnecting: createOwnerScreenFixture({
    fixtureId: "owner.device.reconnecting",
    ownerDeviceState: "reconnecting",
  }),
  "background-limited": createOwnerScreenFixture({
    fixtureId: "owner.device.background-limited",
    ownerDeviceState: "background-limited",
  }),
  unavailable: createOwnerScreenFixture({
    fixtureId: "owner.device.unavailable",
    ownerDeviceState: "unavailable",
  }),
});

export const SHARE_STATE_FIXTURES: Readonly<
  Record<ShareState, OwnerScreenFixture>
> = Object.freeze({
  draft: ownerShareStateFixture("draft"),
  preparing: ownerShareStateFixture("preparing"),
  available: ownerShareStateFixture("available"),
  active: ownerShareStateFixture("active"),
  paused: ownerShareStateFixture("paused"),
  "needs-photos": ownerShareStateFixture("needs-photos"),
  expired: ownerShareStateFixture("expired"),
  revoked: ownerShareStateFixture("revoked"),
});

export const VIEWER_ADMISSION_FIXTURES: Readonly<
  Record<ViewerAdmissionState, ViewerScreenFixture>
> = Object.freeze({
  authorizing: viewerAdmissionFixture("authorizing"),
  "waiting-for-owner": viewerAdmissionFixture("waiting-for-owner"),
  "waiting-for-capacity": viewerAdmissionFixture("waiting-for-capacity"),
  connecting: viewerAdmissionFixture("connecting"),
  connected: viewerAdmissionFixture("connected"),
  reconnecting: viewerAdmissionFixture("reconnecting"),
  unavailable: viewerAdmissionFixture("unavailable"),
});

export const VIEWER_ACTIVITY_FIXTURES: Readonly<
  Record<ViewerActivityState, ViewerScreenFixture>
> = Object.freeze({
  ready: viewerActivityFixture("ready"),
  browsing: viewerActivityFixture("browsing"),
  "downloading-original": viewerActivityFixture("downloading-original"),
});

export const TRANSFER_FIXTURES: Readonly<
  Record<TransferState, ViewerScreenFixture>
> = Object.freeze({
  queued: transferFixture("queued", 0),
  starting: transferFixture("starting", 0),
  transferring: transferFixture("transferring", 0.42),
  verifying: transferFixture("verifying", 1),
  completed: transferFixture("completed", 1),
  cancelled: transferFixture("cancelled", 0.42),
  failed: transferFixture("failed", 0.42),
});

export const VIDEO_CAPABILITY_FIXTURES: Readonly<
  Record<VideoCapabilityState, ViewerScreenFixture>
> = Object.freeze({
  hidden: videoCapabilityFixture("hidden"),
  testing: videoCapabilityFixture("testing"),
  available: videoCapabilityFixture("available"),
  "disabled-during-session": videoCapabilityFixture(
    "disabled-during-session",
  ),
});

function ownerShareStateFixture(state: ShareState): OwnerScreenFixture {
  return createOwnerScreenFixture({
    fixtureId: `owner.share.${state}`,
    shares: [
      {
        ...BASE_SHARES[0]!,
        state,
      },
      BASE_SHARES[1]!,
    ],
  });
}

function viewerAdmissionFixture(
  state: ViewerAdmissionState,
): ViewerScreenFixture {
  const hasGallery = state === "connected" || state === "reconnecting";
  return createViewerScreenFixture({
    fixtureId: `viewer.admission.${state}`,
    admissionState: state,
    selectedShareId: hasGallery ? FIXTURE_IDS.shareA : null,
    shares: hasGallery ? BASE_SHARES : [],
  });
}

function viewerActivityFixture(
  state: ViewerActivityState,
): ViewerScreenFixture {
  return createViewerScreenFixture({
    fixtureId: `viewer.activity.${state}`,
    activityState: state,
    transferState: state === "downloading-original" ? "transferring" : null,
    transferProgress: state === "downloading-original" ? 0.42 : null,
  });
}

function transferFixture(
  state: TransferState,
  progress: number,
): ViewerScreenFixture {
  return createViewerScreenFixture({
    fixtureId: `viewer.transfer.${state}`,
    activityState: state === "completed" || state === "cancelled"
      || state === "failed"
      ? "ready"
      : "downloading-original",
    transferState: state,
    transferProgress: progress,
    videoCapability: "testing",
  });
}

function videoCapabilityFixture(
  state: VideoCapabilityState,
): ViewerScreenFixture {
  return createViewerScreenFixture({
    fixtureId: `viewer.video.${state}`,
    videoCapability: state,
    transferState: state === "disabled-during-session" ? "cancelled" : null,
    transferProgress: null,
  });
}
